import { useContext, useState } from "react";
import { Box, Collapse, Divider, IconButton, Typography } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import classes from "../homePageNew.module.css";
import { AuthContext } from "../../../context/auth-context";
import ShareUserPicker from "../../../Component/ShareModal/ShareUserPicker";

const SHARE_REPORT_ENDPOINT = "/userset/share/transaction";

/**
 * Generic share modal — copy-link row plus, when the account has sharing
 * enabled, the same "share with other users" picker
 * DiscoveryV2/Share/Share.js uses: the real /userset/share/transaction
 * endpoint and the shared ShareUserPicker component, reused as-is (it isn't
 * tied to DiscoveryContext — it only needs item/shareEndpoint/buildPayload).
 *
 * Positioned via .shareModalOverlay (position: fixed), same light
 * overlay theme as ReportsModal.js's own overlay, so it always centers on
 * the viewport regardless of where it's rendered from.
 */
export default function ShareModal({ open, onClose, itemName, documentPath, transactionId, userId }) {
  const { user } = useContext(AuthContext);
  const [isCopied, setIsCopied] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(true);

  if (!open) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(documentPath);
    setIsCopied(true);
    toast.success("Share link is copied.");
    setTimeout(() => setIsCopied(false), 4000);
  };

  const buildSharePayload = (item, targets) => ({
    transaction_id: transactionId,
    user_id: userId,
    customer_id: user?.customer_id,
    targets,
  });

  const parseShareResponse = (data) => [
    ...(data?.shared ?? []).map((s) => ({ ...s, status: "created", final_name: itemName })),
    ...(data?.already_shared ?? []).map((s) => ({ ...s, status: "skipped_self" })),
  ];

  return (
    <div className={classes.shareModalOverlay} onClick={onClose}>
      <div className={classes.shareModalBox} onClick={(e) => e.stopPropagation()}>
        <div className={classes.shareModalInner}>
          <div className={classes.shareModalHead}>
            <span className={classes.shareModalTitle}>{itemName}</span>
            <button className={classes.modalClose} onClick={onClose}>
              ✕
            </button>
          </div>

          <div className={classes.shareModalCopyRow}>
            <span className={classes.shareModalPath}>{documentPath}</span>
            <button className={classes.drawerActionBtn} onClick={handleCopy} title="Click to copy">
              {isCopied ? <DoneOutlinedIcon fontSize="small" color="success" /> : <ContentCopyOutlinedIcon fontSize="small" />}
            </button>
          </div>

          {user?.is_sharing_enabled && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box
                onClick={() => setUsersExpanded((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
              >
                <Typography variant="subtitle2">Share with other users</Typography>
                <IconButton size="small">
                  {usersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={usersExpanded}>
                <Box sx={{ mt: 1 }}>
                  <ShareUserPicker
                    item={{ id: transactionId, name: itemName }}
                    itemTypeLabel="report"
                    shareEndpoint={SHARE_REPORT_ENDPOINT}
                    buildPayload={buildSharePayload}
                    onClose={() => setUsersExpanded(false)}
                    description="Selected users get access to this report in their account. They cannot edit or delete your original."
                    excludeUserId={userId}
                    parseShareResponse={parseShareResponse}
                    alreadyHasAccessMessage="already has access to this report, no action taken"
                  />
                </Box>
              </Collapse>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
}
